import { validateOptions } from './utils';
import { flatten } from './flatten';

/**
 * TYPES
 */

interface BaseAction<T, P> {
  type: T;
  payload: P;
}

interface InitializePayload {
  languages: Array<string | NamedLanguage>;
  options?: Options;
  activeLanguage?: string;
}

interface AddTranslationForLanguagePayload {
  translation: any;
  language: string;
}

interface SetLanguagesPayload {
  languages: Array<string | NamedLanguage>;
  activeLanguage?: string;
  options?: Options;
}

interface SetActiveLanguagePayload {
  languageCode: string;
}

type Action = BaseAction<
  string,
  InitializePayload &
    AddTranslationForLanguagePayload &
    SetActiveLanguagePayload &
    SetLanguagesPayload
>;

interface Language {
  name?: string;
  code: string;
  active: boolean;
}

interface NamedLanguage {
  name: string;
  code: string;
}

interface Translations {
  [key: string]: string[];
}

type TransFormFunction = (data: any, languageCodes: string[]) => Translations;

export interface LocaleState {
  languages: Language[];
  translations: Translations;
  options: Options;
}

export interface Options {
  defaultLanguage?: string;
  showMissingTranslationMsg?: boolean;
  missingTranslationMsg?: string;
  ignoreTranslateChildren?: boolean;
  translationTransform?: TransFormFunction;
  missingTranslationCallback?: (key: string, languageCode: string) => any;
}

interface BaseAction<T, P> {
  type: T;
  payload: P;
}

type SetActiveLanguageAction = BaseAction<
  '@@localize/SET_ACTIVE_LANGUAGE',
  SetActiveLanguagePayload
>;

type ActionDetailed = Action & {
  languageCodes: string[];
  translationTransform: TransFormFunction | undefined;
};

/**
 * ACTIONS
 */
const INITIALIZE = '@@localize/INITIALIZE';
const SET_LANGUAGES = '@@localize/SET_LANGUAGES';
const SET_ACTIVE_LANGUAGE = '@@localize/SET_ACTIVE_LANGUAGE';
const ADD_TRANSLATION = '@@localize/ADD_TRANSLATION';
const ADD_TRANSLATION_FOR_LANGUGE = '@@localize/ADD_TRANSLATION_FOR_LANGUGE';

/**
 * REDUCERS
 */
function translations(
  state: Translations = {},
  action: ActionDetailed,
): Translations {
  switch (action.type) {
    case ADD_TRANSLATION:
      // apply transformation if set in options
      const translations =
        action.translationTransform !== undefined
          ? action.translationTransform(
              action.payload.translation,
              action.languageCodes,
            )
          : action.payload.translation;
      return {
        ...state,
        ...flatten(translations, { safe: true }),
      };
    case ADD_TRANSLATION_FOR_LANGUGE:
      const languageIndex = action.languageCodes.indexOf(
        action.payload.language,
      );
      const flattenedTranslations =
        languageIndex >= 0 ? flatten(action.payload.translation) : {};
      // convert single translation data into multiple
      const languageTranslations = Object.keys(flattenedTranslations).reduce(
        (prev, cur: string) => {
          // loop through each language, and for languages that don't match active language
          // keep existing translation data, and for active language store new translation data
          const translationValues = action.languageCodes.map((code, index) => {
            const existingValues = state[cur] || [];
            return index === languageIndex
              ? // @ts-expect-error-auto TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                flattenedTranslations[cur]
              : existingValues[index];
          });
          return {
            ...prev,
            [cur]: translationValues,
          };
        },
        {},
      );

      return {
        ...state,
        ...languageTranslations,
      };
    default:
      return state;
  }
}

function languages(state: Language[] = [], action: Action): Language[] {
  switch (action.type) {
    case INITIALIZE:
    case SET_LANGUAGES:
      const options = action.payload.options || {};
      const activeLanguage =
        action.payload.activeLanguage || options.defaultLanguage;
      return action.payload.languages.map((language, index) => {
        const isActive = (code: string) => {
          return activeLanguage !== undefined
            ? code === activeLanguage
            : index === 0;
        };
        // check if it's using array of Language objects, or array of languge codes
        return typeof language === 'string'
          ? { code: language, active: isActive(language) } // language codes
          : { ...language, active: isActive(language.code) }; // language objects
      });
    case SET_ACTIVE_LANGUAGE:
      return state.map((language) => {
        return language.code === action.payload.languageCode
          ? { ...language, active: true }
          : { ...language, active: false };
      });
    default:
      return state;
  }
}

const defaultTranslateOptions: Options = {
  showMissingTranslationMsg: true,
  missingTranslationMsg:
    // eslint-disable-next-line no-template-curly-in-string
    'Missing translation key ${ key } for language ${ code } ', // fix key/code later
  ignoreTranslateChildren: false,
};

const initialState: LocaleState = {
  languages: [],
  translations: {},
  options: defaultTranslateOptions,
};
function options(
  state: Options = defaultTranslateOptions,
  action: Action,
): Options {
  switch (action.type) {
    case INITIALIZE:
      const options = action.payload.options || {};
      return {
        ...state,
        ...validateOptions(options),
      };
    default:
      return state;
  }
}

export const localeReducer = (
  state: LocaleState = initialState,
  action: Action,
): LocaleState => {
  const languageCodes = state.languages.map((language) => language.code);
  const translationTransform = state.options.translationTransform;
  return {
    languages: languages(state.languages, action),
    translations: translations(state.translations, {
      ...action,
      languageCodes,
      translationTransform,
    }),
    options: options(state.options, action),
  };
};

/**
 * ACTION CREATORS
 */
export const setActiveLanguage = (
  languageCode: string,
): SetActiveLanguageAction => ({
  type: SET_ACTIVE_LANGUAGE,
  payload: { languageCode },
});
